import {Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {Router} from "@angular/router";
import {Store} from "@ngrx/store";
import * as fromStore from '../../store';
import {filter, first, switchMap} from "rxjs/operators";
import {UserSubscription, UserSubscriptionType} from "../../user/user-subscription";
import {GoogleAnalyticsService} from "../../shared/services/google-analytics/google-analytics.service";
import {DbModalService} from "../../modules/modal/modal";
import {combineLatest} from "rxjs";
import {HostingPasswordProtection} from "../../websites/website/hosting/info/hosting-password-protection";
import {UserService} from "../../user/user.service";
import {User} from "../../user/user";
import {UserSubscriptionStatus} from "../../user/user-subscription-status";
import {clientRoles} from '../../user/user-roles-actions';
import {DbResponse} from "../../shared/interceptors/db-response";
import {HostingService} from "../../websites/website/hosting/hosting.service";
import {FormControl} from "@angular/forms";
import {NotifierComponent} from "../../shared/components/notifier/notifier.component";
import {Notification} from "../../shared/components/notifier/notification";
import {NotificationType} from "../../shared/components/notifier/notification-type";
import {Utils} from "../../shared/utils/utils";

@Component({
    selector: 'db-site-lock',
    templateUrl: './website-lock-on.component.html',
    styleUrls: ['./website-lock-on.component.scss'],
    standalone: false
})
export class SiteLockIsOnComponent implements OnInit, OnChanges {

  @Input() userSubscription: UserSubscription;
  @Input() passwordProtection: HostingPasswordProtection;
  @Input() websiteId: number;
  @Input() websiteUrl: string;
  @Input() isStaging: boolean;
  @Input() user: User;
  @Input() demoUser: boolean;
  @Input() primaryChanged: boolean;
  @Input() isWVCSite: boolean = false;

  public extraMobile: boolean;
  public hasPayments: boolean;
  public userRole: any;
  public toggled = false;
  public isCopiedHostNames: any;
  public isCopiedCodes: any;
  public userLocalStorage: any;
  public clientRoles = clientRoles;
  public UserSubscriptionType = UserSubscriptionType;
  public passProtection: FormControl;
  public percent: number = 0;
  public loading = false;
  public action: string = 'none';

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _router: Router,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _element: ElementRef,
    private _modalService: DbModalService,
    private _userService: UserService,
    private _hostingService: HostingService
  ) {}

  ngOnInit() {
    this.extraMobile = window.innerWidth < 670;
    this.passProtection = new FormControl(!!this.passwordProtection?.username ? 'enable' : 'disable');
    combineLatest(
      this._store.select(fromStore.getUserLoaded),
      this._store.select(fromStore.getUserLocalStorageLoaded),
    )
      .pipe(
        filter(([userLoaded, storageLoaded]) => userLoaded && storageLoaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getPayments),
          this._store.select(fromStore.getWorkspaceIdEntities),
          this._store.select(fromStore.getUserSubscription)
        ))
      )
      .subscribe(([userLocalStorage, payments, workspaceIdEntities, userSubscription]) => {
        this.userLocalStorage = userLocalStorage;
        this.userSubscription = userSubscription;
        this.toggled = !userLocalStorage.passProtectionSeen && payments && payments.length;
        const currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || workspaceIdEntities.ownerWorkspaceId;
        this.userRole = this.user && this.user.workspaces.find(el => el.workspaceId === currentWorkspaceId);
        this.hasPayments = !!payments?.length;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['passwordProtection'] ) {
      this.passProtection = new FormControl(!!this.passwordProtection ? 'enable' : 'disable');
      this.passwordProtection = changes['passwordProtection'].currentValue;
    }
    if (changes && changes['websiteId'] ) {
      this._addEventListeners();
    }
  }

  public showDisablePassProtection(from: string): void {
    this.toggled = false;
    this.setPassProtectionSeen();
    switch (from) {
      case 'point-domain':
        this._router.navigate([`websites/${this.websiteId}/domains`]);
        break;
      case 'upgrade':
        this.redirectToPricingPage();
        break;
      case 'change-settings':
        this._router.navigate([`websites/${this.websiteId}/domains`]);
        break;
      default:
        this.toggled = false;
    }
  }

  public setupPassProtection(action: string): void {
    if (!this.hasPayments) {
      this.showDisablePassProtection('upgrade');
      return;
    }
    this.action = action;
    this.loading = true;
    let fakeProgress = 0;
    // Start a simulated progress
    const progressInterval = setInterval(() => {
      if (fakeProgress < 95) { // Cap progress to 95% to leave space for real completion
        fakeProgress += 5; // You can adjust the speed here
        this.percent = fakeProgress;
      }
    }, 100); // Every 200ms

    this._hostingService.setupPasswordProtection(this.websiteId, action)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first()
      )
      .subscribe((response: DbResponse): void => {
        setTimeout(() => {
          this.percent = 100;
          this.loading = false;
          this.action = 'none';
        }, 500);
        this._store.dispatch(new fromStore.LoadInfo(this.websiteId));
        NotifierComponent.notify(new Notification(Utils.capitalize(response.message), response.success ? NotificationType.SUCCESS : NotificationType.DANGER));
      });
  }

  public setPassProtectionSeen() {
    if (this.userLocalStorage.passProtectionSeen) {
      return;
    }
      this.userLocalStorage = {...this.userLocalStorage, passProtectionSeen: true};
      this._userService.setToLocal(this.userLocalStorage)
        .pipe(
          first(response => !!response),
        )
        .subscribe(response => {
          if (response.success) {
            this._store.dispatch(new fromStore.LoadUserLocalStorage());
          }
        });
  }


  public copiedUserName() {
    this.isCopiedHostNames = true;
    setTimeout(() => {
      this.isCopiedHostNames = false;
    }, 1000);
  }

  public copiedPassword() {
    this.isCopiedCodes = true;
    setTimeout(() => {
      this.isCopiedCodes = false;
    }, 1000);
  }

  public redirectToPricingPage(): void {

    if (this.userSubscription
      .status === UserSubscriptionStatus.canceled) {
      this._router.navigate(['/workspace/subscription-plan']);
    } else {
      if (this.userSubscription?.trialClient) {
        this._router.navigate(['/upgrade-plan']);
      }
      this.passProtection = new FormControl(this.isWVCSite && this.userSubscription?.trialClient ? 'disable' : 'enable');
    }
  }

  private _addEventListeners(): void {
    this._store.select(fromStore.getInfoLoaded(this.websiteId))
      .pipe(
        filter((loaded: boolean) => !!loaded),
        switchMap(() => this._store.select(fromStore.getInfo(this.websiteId))),
      )
      .subscribe((hostingInfo: any): void => {
        if (hostingInfo) {
          this.passwordProtection = hostingInfo && hostingInfo.hostingPasswordProtection;
        }
      });
  }
}
